// Single source of truth for the three buckets shown on the v2 Payments page.
// Tab labels, header tile labels, row chip labels, URL slugs, and the status
// filter sent to the server all derive from this map. If a new label needs to
// be coined or a slug needs to change, this is the only file to touch.
//
// The DB voucher_status enum is the internal vocabulary; these "buckets" are a
// UI-only grouping. GENERATED / CANCELLED / DEACTIVATED vouchers are not part
// of any bucket and are intentionally invisible on the Payments page.

import type { VoucherStatus } from '../../../../data/types';

export type PaymentBucket = 'AWAITING_REVIEW' | 'AWAITING_PAYMENT' | 'PAID';

export interface PaymentBucketDef {
	status: VoucherStatus;
	slug: string;
	i18nKey: string;
	fallback: string;
	color: 'info' | 'warning' | 'success';
}

export const PAYMENT_BUCKETS: Record<PaymentBucket, PaymentBucketDef> = {
	AWAITING_REVIEW: {
		status: 'VERIFIED',
		slug: 'awaiting-review',
		i18nKey: 'payments.bucket.awaitingReview',
		fallback: 'Awaiting Review',
		color: 'info',
	},
	AWAITING_PAYMENT: {
		status: 'READY',
		slug: 'awaiting-payment',
		i18nKey: 'payments.bucket.awaitingPayment',
		fallback: 'Awaiting Payment',
		color: 'warning',
	},
	PAID: {
		status: 'REDEEMED',
		slug: 'paid',
		i18nKey: 'payments.bucket.paid',
		fallback: 'Paid',
		color: 'success',
	},
};

export const PAYMENT_BUCKET_ORDER: PaymentBucket[] = [
	'AWAITING_REVIEW',
	'AWAITING_PAYMENT',
	'PAID',
];

export const DEFAULT_BUCKET: PaymentBucket = 'AWAITING_REVIEW';

const STATUS_TO_BUCKET: Partial<Record<VoucherStatus, PaymentBucket>> = (() => {
	const m: Partial<Record<VoucherStatus, PaymentBucket>> = {};
	for (const key of PAYMENT_BUCKET_ORDER) {
		m[PAYMENT_BUCKETS[key].status] = key;
	}
	return m;
})();

export const bucketForStatus = (s: VoucherStatus | null | undefined): PaymentBucket | null =>
	s ? STATUS_TO_BUCKET[s] ?? null : null;

const SLUG_TO_BUCKET: Record<string, PaymentBucket> = PAYMENT_BUCKET_ORDER.reduce(
	(acc, key) => {
		acc[PAYMENT_BUCKETS[key].slug] = key;
		return acc;
	},
	{} as Record<string, PaymentBucket>
);

/**
 * Resolve a raw `?tab=` query value to a bucket.
 *
 * Only the canonical kebab-case slugs are accepted. Anything else (missing,
 * empty, legacy `ready|pending|redeemed`, unknown garbage) resolves to
 * `DEFAULT_BUCKET` with `needsRedirect: true` so the caller can rewrite the URL.
 */
export const resolveBucketFromSlug = (
	raw: string | null | undefined
): { bucket: PaymentBucket; needsRedirect: boolean } => {
	if (!raw) return { bucket: DEFAULT_BUCKET, needsRedirect: true };
	const hit = SLUG_TO_BUCKET[raw];
	if (hit) return { bucket: hit, needsRedirect: false };
	return { bucket: DEFAULT_BUCKET, needsRedirect: true };
};
